'use client';

import Link from 'next/link';
import PageContainer from '@/layout/page-container';
import Section from '@/layout/section';
import * as React from 'react';
import { PageHero } from '@/modules/marketing/components/page-shared';
import { Button } from '@/components/ui/button';
import { Animated, StaggerContainer } from '@/components/ui/animated';

interface QA { q: string; a: React.ReactNode; group: string; }

const items: QA[] = [
  { group: 'Verification & Quality', q: 'How are preceptors verified?', a: 'Credential checks, activity recency review, and manual approval before onboarding.' },
  { group: 'Verification & Quality', q: 'Do you audit ongoing performance?', a: 'We monitor feedback trends and intervene if expectations or professionalism standards slip.' },
  { group: 'Scheduling & Changes', q: 'Can I change my date range after booking?', a: 'Yes, with preceptor confirmation. Earlier notice improves success of adjustments.' },
  { group: 'Scheduling & Changes', q: 'How far in advance should I reserve a spot?', a: "It's best to start as far in advance as possible to secure the best spots but we help student even after the sessions have started." },
  { group: 'Scheduling & Changes', q: 'Can I change my preceptor?', a: 'Yes. You can ask the team to match you with a better option and we will find the best available preceptor. Any changes will be discussed with you.' },
  { group: 'Scheduling & Changes', q: 'How do I cancel my rotation?', a: 'You let the team know that you would like to cancel your rotation at least 2 weeks before the start date and we will process your refund. In that case, also make sure to withdraw your application with the school so the preceptor can take another student.' },
  { group: 'Documentation', q: 'What documents are required?', a: 'No documents are needed from you. You will recieve the preceptor CV, license and signed paperwork once your rotation is confirmed with the site.' },
  { group: 'Billing & Payment', q: 'What payment methods do you accept?', a: 'Credit/debit cards. Payments are processed through stripe secure systems. We also accept Zelle and Venmo payments.' },
  { group: 'Billing & Payment', q: 'Are there hidden fees?', a: 'No. Pricing is transparent; the moment you select the preceptor. If there is any change in the amount, it will be discussed with you before the application is submitted.' },
  { group: 'Placement Process', q: 'How does the placement process work?', a: (<div className="space-y-3"><p><strong>Step 1:</strong> You select a preceptor from the system, check the rotation details and authorize the deposit payment.</p><p><strong>Step 2:</strong> The team reviews the application and confirms the details. Once it's confirmed, we charge the $200 deposit. This deposit is refundable in many cases, as long as the preceptor has not been approved. If there is any problem with the application, we will get in contact with you to find another suitable option.</p><p><strong>Step 3:</strong> After payment, we share the provider name/site details, and confirm availability with the preceptor.</p><p><strong>Step 4:</strong> Once confirmed, we send everything needed to submit your application (CV, license, email, phone number, etc). To access the signed paperwork, you will have to pay 15% of the remaining amount.</p><p><strong>Step 5:</strong> You submit your application and keep us posted on your approval.</p><p><strong>Step 6:</strong> After approval, we help you get in contact with your preceptor and you pay the remaining balance, due before your clinical start date.</p></div>) },
];

const grouped = items.reduce<Record<string, QA[]>>((acc, item) => {
  acc[item.group] = acc[item.group] || [];
  acc[item.group].push(item);
  return acc;
}, {});

function QAGroup({ label, faqs, delay }: { label: string; faqs: QA[]; delay: number }) {
  return (
    <Animated animation="fade-up" delay={delay}>
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">{label}</h2>
        <div className="space-y-4">
          {faqs.map((f, idx) => (
            <details 
              key={f.q} 
              className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-all duration-300"
              style={{ transitionDelay: `${idx * 50}ms` }}
            >
              <summary className="cursor-pointer list-none p-6 flex items-start justify-between gap-4">
                <span className="font-semibold text-base text-gray-900 pr-8">{f.q}</span>
                <span className="flex-shrink-0 text-gray-400 transition-transform duration-300 group-open:rotate-180">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                </span>
              </summary>
              <div className="px-6 pb-6 text-base text-gray-600 leading-relaxed animate-in fade-in slide-in-from-top-2 duration-300">
                {f.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </Animated>
  );
}

export default function FAQPage() {
  const groups = Object.entries(grouped);
  return (
    <PageContainer>
      <Animated animation="fade-up" duration={800}>
        <PageHero
          kicker="Support"
          title="Frequently Asked Questions"
          subtitle="Operational transparency helps you plan and avoid avoidable delays. Expand the categories below." />
      </Animated>
      <Section>
        <div className="max-w-4xl mx-auto space-y-16">
          {groups.map(([label, faqs], index) => (
            <QAGroup key={label} label={label} faqs={faqs} delay={100 + index * 100} />
          ))}
          
          <Animated animation="fade-up" delay={600}>
            <div className="text-center pt-8 border-t border-gray-200">
              <p className="text-gray-600 mb-6">Still have questions?</p>
              <Link href="/contact">
                <Button size="lg" className="px-8 hover:scale-105 transition-transform">
                  Contact Our Team
                </Button>
              </Link>
            </div>
          </Animated>
        </div>
      </Section>
    </PageContainer>
  );
}
